import { useEffect, useRef, useState } from 'preact/hooks';
import { apiFetch } from '../api/client';

const documents = [['terms', 'Terms of Service'], ['privacy', 'Privacy Policy'], ['sms-terms', 'SMS Terms']];

export function hasAgreementMetadata(agreement) {
  return !!agreement && typeof agreement.version === 'string' && !!agreement.version.trim()
    && typeof agreement.acceptance_text_version === 'string' && !!agreement.acceptance_text_version.trim()
    && typeof agreement.acceptance_text === 'string' && !!agreement.acceptance_text.trim()
    && documents.every(([key]) => {
      const doc = agreement.documents?.[key];
      if (!doc || typeof doc.version !== 'string' || !doc.version.trim() || !/^[a-f0-9]{64}$/i.test(doc.sha256 || '')) return false;
      try { const url = new URL(doc.url); return url.protocol === 'https:' && !url.username && !url.password; }
      catch { return false; }
    });
}

export function AgreementPanel({ agreement, internal = false, disabled = false, onReload }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const identity = JSON.stringify(agreement);
  useEffect(() => { setChecked(false); }, [identity]);

  async function accept() {
    if (pending.current || disabled || !checked || !hasAgreementMetadata(agreement)) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setChecked(false);
    try {
      await apiFetch('/v1/owner/billing/agreement', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accepted: true, version: agreement.version, acceptance_text_version: agreement.acceptance_text_version }),
      });
      // The fresh billing read is the authority, including after a lost response.
      await onReload();
    } catch (err) {
      setError(err.code === 'agreement_changed' ? 'The agreement changed. Review the current documents and confirm again.' : err.status === 409 ? (err.message || 'The account is busy. Refresh billing status before trying again.') : 'Acceptance could not be confirmed. Refresh billing status before trying again.');
      await onReload();
    } finally { pending.current = false; setBusy(false); }
  }

  if (!hasAgreementMetadata(agreement)) return <p role="status">Signup agreement information is unavailable. Refresh billing status to load the current documents before subscribing or continuing setup.</p>;
  return <section aria-label="Salon signup agreement" style={{ marginTop: '1.5rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '1rem' }}>
    <h2 style={{ fontSize: '1.125rem' }}>Salon signup agreement</h2>
    <p>{documents.map(([key, label], index) => <span key={key}>{index > 0 && ' · '}<a href={agreement.documents[key].url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--sage)', textDecoration: 'underline' }}>{label}</a></span>)}</p>
    {agreement.accepted === true ? <p role="status">Your salon agreement is accepted.</p> : <>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 0' }}>
        <input type="checkbox" checked={checked} disabled={busy || disabled} onChange={event => setChecked(event.currentTarget.checked)} style={{ marginTop: '0.35rem', flexShrink: 0 }} />
        <span>{agreement.acceptance_text}</span>
      </label>
      <p style={{ fontSize: '0.875rem' }}>Text-message permissions are requested separately.</p>
      <button type="button" disabled={!checked || busy || disabled} onClick={accept} style={{ minHeight: '44px', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid var(--border-subtle)', background: 'white' }}>{busy ? 'Saving agreement…' : internal ? 'Accept and continue setup' : 'Accept agreement'}</button>
    </>}
    {error && <p role="alert" style={{ color: 'var(--coral)' }}>{error}</p>}
  </section>;
}
